import type { FC } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { Shippers } from 'src/apis/capacity-management/types';
import type { Organisations } from 'src/apis/document-exchange/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

type Props = {
  label: string;
  url: string;
  placeholder: string;
  name?: string;
};

export const shipperOptionSchema = z.object({
  eic: z.string(),
  ...formOptionSchema.shape,
});

type ShipperMetaOptions = z.input<typeof shipperOptionSchema>;

export const ShipperSearchableMultiSelect: FC<Props> = ({
  label,
  url,
  placeholder,
  name = 'shipperId',
}) => {
  const searchRequest = useAxios(
    (axios, baseConfig) =>
      axios.request<Shippers | Organisations>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  const shippers = searchRequest.response?.data._embedded.items.map(
    (shipper) =>
      ({
        value: shipper.id,
        label: shipper.companyName,
        eic: shipper.eic,
      }) satisfies ShipperMetaOptions
  );

  const search = useClientSearch(
    shippers,
    (shipper, search) =>
      shipper.label.toLowerCase().match(search) ||
      shipper.eic.toLowerCase().match(search)
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!search.searchValue || searchRequest.response) return;
    searchRequest.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, [search.searchValue, searchRequest.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name={name}
      stacked
      placeholder={placeholder}
      {...search}
      renderOptionHint={(option) => option.eic}
      renderSelectedOptionTooltip={(option) => (
        <>
          <strong>EIC:</strong> {option.eic}
        </>
      )}
    />
  );
};
